import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchivable from 'archive/isArchivable';
import isArchived from 'archive/isArchived';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {bulkArchive, bulkUnarchive} from 'processes/dao/ProcessManagementDAO';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {withLoadingScreen} from 'util/Promises';

import {ArchiveRecordGraphQL} from '../../../../graphql-fragments/app-core/platform/archivable/__generated/ArchiveRecordGraphQL';

export interface ProcessForProcessArchiveButton {
  id: string;
  name: string;
  archiveRecord: ArchiveRecordGraphQL | null;
}

interface ProcessArchiveButtonProps {
  process: ProcessForProcessArchiveButton;
  forceRefetch: () => Promise<unknown>;
}

function archiveProcess(process: ProcessForProcessArchiveButton, forceRefetch: () => Promise<unknown>): void {
  ArchiveModal.open({
    items: [process],
    onArchive: async (itemIds: [string], purpose: string) => {
      async function doArchive(): Promise<void> {
        await bulkArchive(ModelTypeConstants.PROCESS, itemIds, purpose);
        await forceRefetch();
      }
      await withLoadingScreen(doArchive(), 'Archiving workflow...');
    },
  });
}

async function unarchiveProcess(
  process: ProcessForProcessArchiveButton,
  forceRefetch: () => Promise<unknown>
): Promise<void> {
  async function doUnarchive(): Promise<void> {
    await bulkUnarchive(ModelTypeConstants.PROCESS, [process.id]);
    await forceRefetch();
  }
  await withLoadingScreen(doUnarchive(), 'Unarchiving task group...');
}

export default function ArchiveProcessButton(props: ProcessArchiveButtonProps): JSX.Element {
  const isProcessArchivable = isArchivable(props.process);
  const isProcessArchived = isArchived(props.process);
  return (
    <Tooltip
      title={
        isProcessArchivable
          ? isProcessArchived
            ? 'Unarchive task group'
            : 'Archive task group'
          : 'Task group cannot be archived.'
      }
    >
      <Button
        buttonStyle={isProcessArchived ? ButtonStyle.SECONDARY : ButtonStyle.TERTIARY_GRAY}
        className={classNames('u-mini-spacing-left', css(styles.archiveButton))}
        disabled={!isProcessArchivable}
        icon={isProcessArchived ? 'unarchive' : 'archive'}
        iconAltText={isProcessArchived ? 'Unarchive' : 'Archive'}
        onClick={() => {
          (isProcessArchived ? unarchiveProcess : archiveProcess)(props.process, props.forceRefetch);
        }}
        size={ButtonSize.MINI}
      />
    </Tooltip>
  );
}

const styles = StyleSheet.create({
  archiveButton: {
    height: '28px',
    width: '28px',
  },
});
